<template>
  <div class="page-container">
    <div class="main-content">
      <div class="page-inner-box">
        <h1 class="cart-title">我的购物车</h1>
        
        <!-- 购物车为空状态 -->
        <div v-if="cartItems.length === 0" class="empty-cart">
          <i class="el-icon-shopping-cart-empty"></i>
          <p class="empty-text">购物车是空的</p>
          <el-button type="primary" @click="goShopping">去购物</el-button>
        </div>
        
        <!-- 购物车列表 -->
        <div v-else class="cart-list">
          <div class="cart-header">
            <div class="col-check">选择</div>
            <div class="col-info">商品信息</div>
            <div class="col-price">单价</div>
            <div class="col-quantity">数量</div>
            <div class="col-total">小计</div>
            <div class="col-action">操作</div>
          </div>
          
          <!-- 购物车商品项 -->
          <div 
            v-for="item in cartItems" 
            :key="item.id" 
            class="cart-item"
          >
            <div class="col-check">
              <el-checkbox v-model="item.checked" @change="handleSelectionChange(item)"></el-checkbox>
            </div>
            <div class="col-info">
              <div class="item-image">
                <img :src="item.image" alt="商品图片">
              </div>
              <div class="item-details">
                <div class="item-title">{{ item.title }}</div>
                <div class="item-props" v-if="item.props">
                  <span v-for="(prop, i) in item.props" :key="i">{{ prop }} </span>
                </div>
              </div>
            </div>
            <div class="col-price">¥{{ item.price.toFixed(2) }}</div>
            <div class="col-quantity">
              <el-input-number 
                v-model="item.quantity" 
                :min="1" 
                :max="item.stock" 
                @change="updateQuantity(item)"
              ></el-input-number>
            </div>
            <div class="col-total">¥{{ (item.price * item.quantity).toFixed(2) }}</div>
            <div class="col-action">
              <el-button type="text" @click="removeItem(item.id)">删除</el-button>
            </div>
          </div>
          
          <!-- 购物车底部 -->
          <div class="cart-footer">
            <div class="footer-left">
              <el-checkbox v-model="allChecked" @change="toggleAll">全选</el-checkbox>
              <el-button type="text" @click="removeSelected">删除选中商品</el-button>
            </div>
            <div class="footer-right">
              <div class="total-info">
                <span class="total-text">合计：</span>
                <span class="total-price">¥{{ totalPrice.toFixed(2) }}</span>
              </div>
              <el-button type="primary" class="checkout-btn" @click="checkout">去结算</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  getCartList, 
  updateCartItemQuantity, 
  deleteCartItem, 
  batchDeleteCartItems, 
  selectAllCartItems,
  updateCartItemSelected
} from '@/api/cartApi'

export default {
  name: 'AppCart',
  data() {
    return {
      cartItems: [],
      allChecked: false,
      loading: false
    }
  },
  computed: {
    totalPrice() {
      // API returns item with subtotal, but we can also calculate locally
      return this.cartItems
        .filter(item => item.checked)
        .reduce((total, item) => total + (item.price * item.quantity), 0)
    },
    selectedCount() {
      return this.cartItems.filter(item => item.checked).length
    }
  },
  created() {
    this.loadCartItems()
  },
  methods: {
    // 加载购物车商品
    async loadCartItems() {
      this.loading = true
      try {
        const res = await getCartList()
        const list = res.data || []
        // Map API response to view model
        this.cartItems = list.map(item => ({
          id: item.id,
          itemId: item.itemId,
          itemType: item.itemType,
          title: item.itemName,
          image: item.itemImage,
          price: item.currentPrice, // Or finalPrice?
          quantity: item.quantity,
          checked: item.selected,
          stock: item.stock
        }))
        this.checkAllStatus()
        // Update header cart count
        window.dispatchEvent(new Event('storage'))
      } catch (err) {
        this.$message.error(err.message || '获取购物车失败')
      } finally {
        this.loading = false
      }
    },
    
    // 检查全选状态
    checkAllStatus() {
      if (this.cartItems.length === 0) {
        this.allChecked = false
      } else {
        this.allChecked = this.cartItems.every(item => item.checked)
      }
    },
    
    // 切换全选
    async toggleAll(val) {
      try {
        await selectAllCartItems(val ? 1 : 0)
        this.cartItems.forEach(item => {
          item.checked = val
        })
      } catch (err) {
        this.$message.error(err.message || '操作失败')
        // Revert on error
        this.allChecked = !val
      }
    },

    // 切换单个选中状态
    async handleSelectionChange(item) {
      try {
        await updateCartItemSelected(item.id, item.checked ? 1 : 0)
        this.checkAllStatus()
      } catch (err) {
        item.checked = !item.checked
        this.$message.error(err.message || '操作失败')
      }
    },
    
    // 更新商品数量
    async updateQuantity(item) {
      try {
        await updateCartItemQuantity(item.id, item.quantity)
        // 触发storage事件，让Header组件更新购物车数量
        window.dispatchEvent(new Event('storage'))
      } catch (err) {
        this.$message.error(err.message || '更新数量失败')
        // Reload to revert
        this.loadCartItems()
      }
    },
    
    // 移除商品
    removeItem(id) {
      this.$confirm('确定要删除这个商品吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteCartItem(id)
          this.cartItems = this.cartItems.filter(item => item.id !== id)
          this.checkAllStatus()
          this.$message.success('删除成功')
          window.dispatchEvent(new Event('storage'))
        } catch (err) {
          this.$message.error(err.message || '删除失败')
        }
      }).catch(() => {
        // 取消删除
      })
    },
    
    // 删除选中商品
    removeSelected() {
      const selectedItems = this.cartItems.filter(item => item.checked)
      if (selectedItems.length === 0) {
        this.$message.warning('请选择要删除的商品')
        return
      }
      
      this.$confirm('确定要删除选中的商品吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const ids = selectedItems.map(item => item.id)
        try {
          await batchDeleteCartItems(ids)
          this.cartItems = this.cartItems.filter(item => !item.checked)
          this.checkAllStatus()
          this.$message.success('删除成功')
          window.dispatchEvent(new Event('storage'))
        } catch (err) {
          this.$message.error(err.message || '删除失败')
        }
      }).catch(() => {
        // 取消删除
      })
    },
    
    // 去购物
    goShopping() {
      this.$router.push('/study')
    },
    
    // 去结算
    checkout() {
      const selectedItems = this.cartItems.filter(item => item.checked)
      if (selectedItems.length === 0) {
        this.$message.warning('请选择要结算的商品')
        return
      }
      
      // Navigate to checkout confirmation page
      // We can pass cartIds or just let the checkout page fetch selected items
      // But usually passing cartIds is safer or just fetching selected.
      // Since we have an API to get selected items, or we can just rely on cart state.
      // But the checkout API requires cartIds.
      const cartIds = selectedItems.map(item => item.id)
      
      // Store in localStorage for the checkout page to pick up, or pass as query
      localStorage.setItem('checkoutCartIds', JSON.stringify(cartIds))
      
      this.$router.push({
        path: '/cart/checkout',
        query: {
          from: 'cart'
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding-top: 20px;
  padding-bottom: 40px;
}

.page-inner-box {
  max-width: 1200px;
  margin: 0 auto;
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  overflow: visible;
}

.cart-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
}

/* 空购物车样式 */
.empty-cart {
  text-align: center;
  padding: 60px 0;
  color: #999;
  
  .el-icon-shopping-cart-empty {
    font-size: 60px;
    margin-bottom: 20px;
    color: #ccc;
  }
  
  .empty-text {
    font-size: 16px;
    margin-bottom: 30px;
  }
}

/* 购物车列表 */
.cart-list {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: auto;
  min-width: 900px;
}

/* 购物车表头 */
.cart-header {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
  font-weight: 500;
  color: #606266;
  white-space: nowrap;
}

/* 购物车列样式 */
.col-check {
  width: 80px;
  text-align: center;
  flex-shrink: 0;
}

.col-info {
  flex: 0 0 35%;
  display: flex;
  align-items: center;
}

.col-price {
  width: 120px;
  text-align: center;
  flex-shrink: 0;
  position: relative;

}

.col-quantity {
  width: 100px;
  text-align: center;
  flex-shrink: 0;

}

.col-total {
  width: 120px;
  text-align: center;
  font-weight: 500;
  color: #3E2CCD;
  flex-shrink: 0;
  margin-left: 100px;
}

.col-action {
  width: 100px;
  text-align: center;
  flex-shrink: 0;
  margin-right: 20px;

}

/* 购物车商品项 */
.cart-item {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e4e7ed;
  transition: background-color 0.2s;
  white-space: nowrap;

  
  &:hover {
    background-color: #fafafa;
  }
}

/* 商品信息 */
.item-image {
  width: 80px;
  height: 80px;
  margin-right: 15px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
  }
}

.item-details {
  flex: 1;
  
  .item-title {
    font-size: 14px;
    color: #333;
    margin-bottom: 8px;
    line-height: 1.4;
  }
  
  .item-props {
    font-size: 12px;
    color: #999;
  }
}

/* 购物车底部 */
.cart-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: #f5f7fa;
  border-top: 1px solid #e4e7ed;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.footer-right {
  display: flex;
  align-items: center;
  gap: 30px;
}

.total-info {
  display: flex;
  align-items: baseline;
  
  .total-text {
    font-size: 14px;
    color: #606266;
  }
  
  .total-price {
    font-size: 20px;
    font-weight: bold;
    color: #f56c6c;
    margin-left: 10px;
  }
}

.checkout-btn {
  min-width: 120px;
  height: 40px;
  background-color: #3E2CCD;
  border-color: #3E2CCD;
  
  &:hover {
    background-color: #3222a8;
    border-color: #3222a8;
  }
}
</style>
